import { useState, type CSSProperties } from 'react'
import { Link } from 'react-router-dom'
import { AnimatePresence, motion } from 'motion/react'
import { apiErrorMessage, apiUrl, createInvite, type InviteOut } from '@/api/client'
import { AppShell } from '@/components/layout/AppShell'
import { LoadingButton } from '@/components/spectrumui/loading-button'
import { TextField, TextAreaField } from '@/components/form/fields'
import { INVITE_TEMPLATES, type InviteTemplate } from '@/data/invite-templates'
import { EASE_OUT } from '@/lib/ease'
import { formatDateTime } from '@/lib/dashboard-utils'
import { isValidSlug, slugify, SLUG_MAX, SLUG_MIN } from '@/lib/slug'
import { Toaster } from '@/components/ui/toaster'
import { useToast } from '@/hooks/use-toast'
import { usePageMeta } from '@/hooks/use-page-meta';

const MIN_QUESTIONS = 2
const MAX_QUESTIONS = 5

// Same shape the server enforces (models/schemas.py): anything looser would
// only surface as a 422 after the recruiter thinks the invite was created.
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

// Per-question identity for the enter/exit rows. The questions are local-only
// state (the invite sends the trimmed texts), so identity never leaks to the
// API — it only keys the rows.
type QuestionEntry = { id: number, text: string }
let questionIdCounter = 0
const nextQuestionId = () => ++questionIdCounter

// Link-lifetime choices, in hours. 24h is the product default; the API
// accepts 1–720 (30 days) if a custom value is ever wanted.
const EXPIRY_OPTIONS = [
  { hours: 4, label: '4 hours' },
  { hours: 24, label: '24 hours' },
  { hours: 72, label: '3 days' },
  { hours: 168, label: '7 days' },
]
const DEFAULT_EXPIRY_HOURS = 24

export function InviteBuilder() {
  usePageMeta("New interview invite — Aura Recruiter")
  const { toast } = useToast()
  const [title, setTitle] = useState('')
  // null = untouched, so the link name tracks the title; any string (including
  // '') means the recruiter edited it and auto-fill must stop fighting them.
  const [linkName, setLinkName] = useState<string | null>(null)
  const [context, setContext] = useState('')
  const [questions, setQuestions] = useState<QuestionEntry[]>(() =>
    ['', ''].map(text => ({ id: nextQuestionId(), text })),
  )
  const [expiresInHours, setExpiresInHours] = useState<number>(DEFAULT_EXPIRY_HOURS)
  const [candidateEmail, setCandidateEmail] = useState('')
  const [proctoring, setProctoring] = useState(false)
  const [creating, setCreating] = useState(false)
  const [created, setCreated] = useState<InviteOut | null>(null)
  const [copied, setCopied] = useState(false)

  const trimmed = questions.map(q => q.text.trim())
  const email = candidateEmail.trim().toLowerCase()
  const emailValid = email === '' || EMAIL_RE.test(email)
  // While untouched the field mirrors the title, but only when the derived
  // name is itself valid — a short title ("QA" → "qa") must fall back to the
  // random link rather than disabling submit on a field nobody edited.
  const derivedLinkName = slugify(title)
  const effectiveLinkName = linkName ?? (isValidSlug(derivedLinkName) ? derivedLinkName : '')
  // The backend strips before validating (_clean_slug in models/schemas.py), so
  // '  backend-screen  ' is an accepted 'backend-screen' and whitespace-only
  // means "no slug". Trim before validating and sending — like the sibling
  // email field — rather than blocking on characters the recruiter can't see.
  const linkNameValue = effectiveLinkName.trim()
  // An empty link name is allowed — it means the random, unguessable link.
  const slugValid = linkNameValue === '' || isValidSlug(linkNameValue)
  const valid =
    title.trim().length > 0 &&
    trimmed.every(q => q.length > 0) &&
    trimmed.length >= MIN_QUESTIONS &&
    emailValid &&
    slugValid

  const setQuestion = (id: number, value: string) => {
    setQuestions(qs => qs.map(q => (q.id === id ? { ...q, text: value } : q)))
  }

  const addQuestion = () => {
    if (questions.length < MAX_QUESTIONS) setQuestions(qs => [...qs, { id: nextQuestionId(), text: '' }])
  }

  const removeQuestion = (id: number) => {
    if (questions.length > MIN_QUESTIONS) setQuestions(qs => qs.filter(q => q.id !== id))
  }

  // Fills only blank fields (whitespace counts as blank) and swaps the
  // question list whole — templates carry exactly MAX_QUESTIONS entries, so
  // there is no merge path to keep straight.
  const applyTemplate = (template: InviteTemplate) => {
    if (!title.trim()) setTitle(template.title)
    if (!context.trim() && template.context) setContext(template.context)
    setQuestions(template.questions.map(text => ({ id: nextQuestionId(), text })))
  }

  const submit = async () => {
    setCreating(true)
    try {
      const invite = await createInvite({
        title: title.trim(),
        context: context.trim() || undefined,
        questions: trimmed,
        expires_in_hours: expiresInHours,
        proctoring,
        candidate_email: email || undefined,
        slug: linkNameValue || undefined,
      })
      setCreated(invite)
    } catch (error) {
      toast({
        title: 'Creation failed',
        description: apiErrorMessage(error, 'Could not create the interview.'),
        variant: 'destructive',
      })
    } finally {
      setCreating(false)
    }
  }

  const inviteLink = created ? `${window.location.origin}/invite/${created.token}` : ''

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      window.prompt('Copy this link:', inviteLink)
    }
  }

  if (created) {
    return (
      <AppShell active="recruiter">
        <Toaster />
        {/* is-created: the success screen stages its own entrance (head →
            panel → aside) instead of riding the plain page rise. */}
        <div className="wnew-grid is-created">
          <header className="screen-head">
            <div>
              <h1>Send the private link.</h1>
              <p>
                Only the first candidate who opens this link and signs in can take the interview.
                The report and audio recording will appear in your dashboard when they're done.
              </p>
            </div>
          </header>

          <section className="panel wform-panel" aria-label="Created interview">
            <div className="field">
              <span className="field-label">{created.title}</span>
              <ol className="wquestion-list-static">
                {created.questions.map((q, i) => (
                  <li key={i} style={{ '--i': i } as CSSProperties}>{q}</li>
                ))}
              </ol>
            </div>
            <div className="field">
              <label htmlFor="invite-link">Invite link</label>
              <div className="wlink-row">
                <input
                  id="invite-link"
                  readOnly
                  className="wfield-input wlink-input"
                  value={inviteLink}
                  onFocus={e => e.target.select()}
                />
                <button type="button" className="wbtn wbtn-primary" onClick={copyLink}>
                  <span className="wcopy-swap" key={copied ? 'copied' : 'copy'}>
                    {copied ? 'Copied!' : 'Copy link'}
                  </span>
                </button>
              </div>
              {created.candidate_email && (
                <div className="field">
                  <span className="field-label">Emailed to</span>
                  <span className="hint">
                    {created.candidate_email} received the link and the deadline. You can
                    still share the link yourself — the first sign-in claims it.
                  </span>
                </div>
              )}
              {created.expires_at && (
                <span className="hint">
                  This link stops working {formatDateTime(created.expires_at)}.{' '}
                  <a href={apiUrl(`/invite/${created.token}/calendar.ics`)}>
                    Add the deadline to your calendar
                  </a>.
                </span>
              )}
            </div>
            <div className="form-actions wform-actions">
              <Link to="/recruiter" className="wbtn wbtn-secondary">Back to dashboard</Link>
              <Link to="/recruiter/new" className="wbtn wbtn-primary">Create another</Link>
            </div>
          </section>

          <aside className="wnew-aside" aria-label="What happens next">
            <section className="panel waside-card">
              <h2>What happens next</h2>
              <div className="wsteps">
                <div className="wstep"><span className="wstep-num" aria-hidden="true">1</span><div><strong>Share the link</strong><span>Send it to your candidate over email or chat.</span></div></div>
                <div className="wstep"><span className="wstep-num" aria-hidden="true">2</span><div><strong>Candidate interviews</strong><span>They answer your questions on their own time.</span></div></div>
                <div className="wstep"><span className="wstep-num" aria-hidden="true">3</span><div><strong>Review the report</strong><span>Scores, transcript, and the audio recording land here.</span></div></div>
              </div>
            </section>
          </aside>
        </div>
      </AppShell>
    )
  }

  return (
    <AppShell active="recruiter">
      <Toaster />
      <div className="wnew-grid">
        <header className="screen-head">
          <div>
            <h1>Build a focused interview</h1>
            <p>
              The AI interviewer asks exactly these questions — 2–5 keeps each interview
              focused and inside your monthly quota.
            </p>
          </div>
        </header>

        <form
          className="panel wform-panel"
          aria-label="Interview setup"
          onSubmit={e => {
            e.preventDefault()
            if (valid && !creating) submit()
          }}
        >
            <div className="field">
              <TextField
                id="invite-title"
                label={<>Role / position<span className="required" aria-hidden="true">*</span></>}
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                maxLength={200}
              />
              <span className="hint">Shown to the candidate on the invite.</span>
            </div>

            <div className="field">
              <TextField
                id="invite-slug"
                label="Link name (optional)"
                type="text"
                value={effectiveLinkName}
                onChange={e => setLinkName(e.target.value)}
                maxLength={SLUG_MAX}
                aria-invalid={!slugValid}
              />
              <span className="hint">
                {linkNameValue === ''
                  ? 'Leave this empty for a random, private link.'
                  : slugValid
                    ? `${window.location.origin}/invite/${linkNameValue}`
                    : `Use ${SLUG_MIN}–${SLUG_MAX} lowercase letters, numbers, and single hyphens.`}
              </span>
            </div>

            <div className="field">
              <TextAreaField
                id="invite-context"
                label="Job description or context (optional)"
                value={context}
                onChange={e => setContext(e.target.value)}
                rows={4}
                maxLength={4000}
              />
            </div>

            <div className="field">
              <span className="field-label">Start from a template</span>
              <div className="wchips" role="group" aria-label="Question templates">
                {INVITE_TEMPLATES.map(t => (
                  <button key={t.id} type="button" className="chip" onClick={() => applyTemplate(t)}>
                    {t.label}
                  </button>
                ))}
              </div>
              <span className="hint">Fills a blank title and context, and loads five questions — everything stays editable; picking another template swaps the list.</span>
            </div>

            <div className="field">
              <span className="field-label" id="questions-label">Questions <span className="hint" style={{ fontWeight: 500 }}>({questions.length} added · {MIN_QUESTIONS}–{MAX_QUESTIONS} required)</span></span>
              <div className="wquestion-list" aria-labelledby="questions-label">
                {/* AnimatePresence: rows rise in on add and fade out on
                    remove (opacity/y only — layout glides via `layout`),
                    instead of the list snapping. Rows are id-keyed (per-entry
                    identity from the counter above), so a removal exits its
                    own slot while the remaining rows keep their values —
                    no slot shifting. */}
                <AnimatePresence initial={false} mode="popLayout">
                {questions.map((q, idx) => (
                  <motion.div
                    className="wquestion-row"
                    key={q.id}
                    layout
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.2, ease: EASE_OUT }}
                  >
                    <span className="wquestion-num" aria-hidden="true">{String(idx + 1).padStart(2, '0')}</span>
                    <input
                      type="text"
                      placeholder="Write a question for the candidate"
                      value={q.text}
                      onChange={e => setQuestion(q.id, e.target.value)}
                      maxLength={500}
                      aria-label={`Question ${idx + 1}`}
                    />
                    {questions.length > MIN_QUESTIONS && (
                      <button
                        type="button"
                        className="wquestion-remove"
                        onClick={() => removeQuestion(q.id)}
                        aria-label={`Remove question ${idx + 1}`}
                      >
                        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>
                      </button>
                    )}
                  </motion.div>
                ))}
                </AnimatePresence>
              </div>
            </div>

            <div className="field">
              <label htmlFor="invite-expiry">Link expires after</label>
              <select
                id="invite-expiry"
                className="wfield-input"
                value={expiresInHours}
                onChange={e => setExpiresInHours(Number(e.target.value))}
              >
                {EXPIRY_OPTIONS.map(opt => (
                  <option key={opt.hours} value={opt.hours}>
                    {opt.label}{opt.hours === DEFAULT_EXPIRY_HOURS ? ' (default)' : ''}
                  </option>
                ))}
              </select>
              <span className="hint">The candidate must start the interview within this window — after it passes the link stops working.</span>
            </div>

            <div className="field">
              <TextField
                id="invite-email"
                label="Candidate email (optional)"
                type="email"
                value={candidateEmail}
                onChange={e => setCandidateEmail(e.target.value)}
                maxLength={254}
              />
              <span className="hint">
                {emailValid
                  ? "We'll email the link and deadline to the candidate — you can still share the link yourself."
                  : 'Enter a valid email address, or leave this empty to share the link yourself.'}
              </span>
            </div>

            <div className="field">
              <label className="wcheck-row" htmlFor="invite-proctoring">
                <input
                  id="invite-proctoring"
                  type="checkbox"
                  checked={proctoring}
                  onChange={e => setProctoring(e.target.checked)}
                />
                <span>Record integrity signals</span>
              </label>
              <span className="hint">
                Flags tab switches, paste attempts, and unusual answer patterns (long
                dead air, repeated takes, AI-sounding answers) in the report for your
                review. The candidate is told before the interview starts.
              </span>
            </div>

            <div className="form-actions wform-actions">
              {questions.length < MAX_QUESTIONS ? (
                <button type="button" className="wbtn wbtn-secondary" onClick={addQuestion}>
                  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>
                  Add question
                </button>
              ) : <span />}
              <LoadingButton type="submit" size="lg" loading={creating} disabled={!valid}>
                Create interview &amp; link
              </LoadingButton>
            </div>
            {!valid && (
              <p className="hint wform-hint">
                {!slugValid
                  ? 'Fix the link name to continue — or clear it for a random, private link.'
                  : emailValid
                    ? `Add a role name and ${MIN_QUESTIONS}–${MAX_QUESTIONS} non-empty questions to continue.`
                    : 'Fix the candidate email to continue — or empty the field to share the link yourself.'}
              </p>
            )}
        </form>

        <aside className="wnew-aside" aria-label="How it works">
          <section className="panel waside-card">
            <h2>How it works</h2>
            <div className="wsteps">
              <div className="wstep"><span className="wstep-num" aria-hidden="true">1</span><div><strong>Ground the role</strong><span>Role, focus, and context shape the interview.</span></div></div>
              <div className="wstep"><span className="wstep-num" aria-hidden="true">2</span><div><strong>Send a private link</strong><span>The candidate completes it on their own time.</span></div></div>
              <div className="wstep"><span className="wstep-num" aria-hidden="true">3</span><div><strong>Review the report</strong><span>Scores, transcript, and audio, per candidate.</span></div></div>
            </div>
          </section>
          <section className="panel waside-card">
            <h2>Recruiter tip</h2>
            <div className="wsteps">
              <div className="wstep"><span className="wstep-num" aria-hidden="true">01</span><div><strong>Keep the scope focused</strong><span>Two or three well-chosen questions beat a long, scattered set.</span></div></div>
            </div>
          </section>
        </aside>
      </div>
    </AppShell>
  )
}
